// front-end/src/components/block-system/block-manager/block-controls.js

import { getAvailableBlockTypes, getBlockDefinition, blockRequiresPage, validateBlockPageContext } from '../block-configs';
import { verifyAuth, getAuthToken } from '../../auth';
import { addNewBlock } from './block-editor';
import { getBaseType } from '../block-configs';

// УБИРАЕМ ОГРАНИЧЕНИЯ: Разрешаем добавление блоков во все секции
function canAddBlocksToSection(sectionElement) {
    const sectionId = sectionElement.id || sectionElement.getAttribute('class');
    const baseType = getBaseType(sectionId);

    // Запрещаем добавление новостных блоков на главной странице
    if (document.body.getAttribute('data-page') === 'Home' && baseType === 'news-section') {
        return false;
    }

    // Разрешаем новостные блоки только на странице news-feed
    if (baseType === 'news-section' && !window.location.pathname.match(/news-feed(\.html)?$/)) {
        return false;
    }

    return true;
}

export function createSectionControls(sectionElement) {
    verifyAuth(getAuthToken())
        .then(() => {
            // Проверяем, можно ли добавлять блоки
            if (!canAddBlocksToSection(sectionElement)) {
                console.log('Block addition not allowed for this section');
                return;
            }

            // Используем id если есть, иначе используем класс
            const sectionId = sectionElement.id || sectionElement.getAttribute('class');
            const availableBlocks = getAvailableBlockTypes(sectionId);

            // Если нет доступных блоков для этой секции, не показываем контролы
            if (availableBlocks.length === 0) {
                console.log(`No available blocks for section: ${sectionId}`);
                return;
            }

            const controlPanel = document.createElement('div');
            controlPanel.className = 'section-controls';
            controlPanel.style.cssText = `
                margin-bottom: 15px;
                background-color: #f8f9fa;
                border-radius: 8px;
                padding: 10px;
                display: flex;
                gap: 10px;
                align-items: center;
                flex-wrap: wrap;
            `;

            // Показываем название секции
            let sectionTitle = `Section: ${sectionId}`;

            // Для news-content показываем контекст страницы
            if (sectionId === 'news-content' || sectionId.includes('news-content')) {
                const pageContext = determinePageContext(sectionElement);
                if (pageContext) {
                    sectionTitle += ` (Page: ${pageContext})`;
                } else {
                    sectionTitle += ' (⚠️ No page context)';
                }
            }

            // НОВОЕ: Для course-modules-section показываем контекст курса
            if (sectionId === 'course-modules-section' || sectionId.includes('course-modules-section')) {
                const pageContext = determineCoursePageContext(sectionElement);
                if (pageContext) {
                    sectionTitle = `Модулі курсу (Course: ${pageContext})`;
                } else {
                    sectionTitle = 'Модулі курсу (⚠️ No course context)';
                }
            }

            // Для news-section на news-feed показываем специальную информацию
            const baseType = getBaseType(sectionId);
            if (baseType === 'news-section' && window.location.pathname.match(/news-feed(\.html)?$/)) {
                sectionTitle = 'News Management Section';
            }

            const sectionTitleElement = document.createElement('span');
            sectionTitleElement.textContent = sectionTitle;
            sectionTitleElement.style.fontWeight = '500';
            sectionTitleElement.style.marginRight = 'auto';

            controlPanel.appendChild(sectionTitleElement);

            // Проверяем доступность блоков в текущем контексте
            const validBlocks = availableBlocks.filter(blockType => {
                if (blockRequiresPage && blockRequiresPage(blockType)) {
                    const pageValidation = validateBlockPageContext(blockType, sectionElement);
                    return pageValidation.valid;
                }
                return true;
            });

            if (validBlocks.length === 0) {
                const warningText = document.createElement('span');
                warningText.textContent = 'No blocks available in current context';
                warningText.style.color = '#ff9800';
                warningText.style.fontSize = '12px';
                controlPanel.appendChild(warningText);
            } else if (validBlocks.length > 1) {
                const blockSelect = createBlockTypeSelect(validBlocks);
                const addButton = createStyledButton('Add Block', () => {
                    const selectedBlockType = blockSelect.value;
                    addNewBlock(sectionElement, selectedBlockType);
                });

                controlPanel.appendChild(blockSelect);
                controlPanel.appendChild(addButton);
            } else {
                // Для новостных блоков на news-feed добавляем специальную кнопку
                if (baseType === 'news-section' && window.location.pathname.match(/news-feed(\.html)?$/)) {
                    controlPanel.appendChild(
                        createStyledButton('Add News Article', () => addNewBlock(sectionElement, validBlocks[0]))
                    );
                } else if (baseType === 'course-modules-section') {
                    // НОВОЕ: Специальная кнопка для модулей курса
                    controlPanel.appendChild(
                        createStyledButton('Додати модуль', () => {
                            const newModule = addNewBlock(sectionElement, validBlocks[0]);
                            if (newModule) {
                                // Дополнительная логика после создания модуля
                                console.log(`[Controls] Created new course module: ${newModule.getAttribute('data-block-name')}`);
                            }
                        })
                    );
                } else {
                    controlPanel.appendChild(
                        createStyledButton('Add Block', () => addNewBlock(sectionElement, validBlocks[0]))
                    );
                }
            }

            sectionElement.parentNode.insertBefore(controlPanel, sectionElement);
        })
        .catch(error => {
            console.error('[BlockSystem] Authorization error for section panel:', error);
        });
}

// Определение контекста страницы для отображения
function determinePageContext(sectionElement) {
    const path = window.location.pathname;

    // Clean URL: /news/{slug}
    const newsMatch = path.match(/^\/news\/([^/]+)/);
    if (newsMatch) return newsMatch[1];

    // Clean URL: /case/{slug}
    const caseMatch = path.match(/^\/case\/([^/]+)/);
    if (caseMatch) return caseMatch[1];

    // Legacy URL: /News.html?slug=X
    if (path.match(/\/News(\.html)?$/)) {
        const slug = new URLSearchParams(window.location.search).get('slug');
        if (slug) return slug;
    }

    // Ищем ближайший news-block
    let parent = sectionElement.parentElement;
    while (parent) {
        const newsBlock = parent.querySelector('[data-block-name^="news-block-"]');
        if (newsBlock) {
            return newsBlock.getAttribute('data-news-slug') || newsBlock.getAttribute('data-block-name');
        }
        parent = parent.parentElement;
    }

    return null;
}

// НОВАЯ ФУНКЦИЯ: Определение контекста курса
function determineCoursePageContext(sectionElement) {
    // Проверяем, находимся ли мы на странице курса
    if (window.location.pathname.match(/course-detail(\.html)?$/)) {
        const urlParams = new URLSearchParams(window.location.search);
        const courseSlug = urlParams.get('slug');

        if (courseSlug) {
            return courseSlug;
        }
    }

    // Проверяем data-page у body
    const bodyPage = document.body.getAttribute('data-page');
    if (bodyPage && bodyPage.startsWith('Course - ')) {
        return bodyPage.replace('Course - ', '');
    }

    return null;
}

function createBlockTypeSelect(blockTypes) {
    const select = document.createElement('select');
    select.style.cssText = `
        padding: 8px;
        border-radius: 4px;
        border: 1px solid #ccc;
    `;

    blockTypes.forEach(blockType => {
        const option = document.createElement('option');
        const definition = getBlockDefinition(blockType);
        option.value = blockType;
        option.textContent = definition?.title || blockType;
        select.appendChild(option);
    });

    return select;
}

function createStyledButton(text, onClick) {
    const button = document.createElement('button');
    button.textContent = text;
    button.style.cssText = `
        padding: 8px 16px;
        background-color: #4285f4;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: background-color 0.3s;
    `;
    button.addEventListener('mouseover', () => button.style.backgroundColor = '#3367d6');
    button.addEventListener('mouseout', () => button.style.backgroundColor = '#4285f4');
    button.addEventListener('click', onClick);
    return button;
}